import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AppEmojiPicker from '../appEmojiPicker/AppEmojiPicker';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import {
  QUICK_MESSAGE_REACTIONS,
  getMessageReactionGroups,
  normalizeMessageReaction,
} from '../../utils/messageReactions';

const messageReactionPickerStyle = {
  width: 'min(340px, calc(100vw - 32px))',
  height: 'min(360px, 62vh)',
};

const getViewportPaneRect = () => ({
  left: 0,
  right: window.innerWidth || document.documentElement.clientWidth || 0,
  top: 0,
  bottom: window.innerHeight || document.documentElement.clientHeight || 0,
});

const getVisiblePaneRect = (paneRect) => {
  const viewportRect = getViewportPaneRect();
  const left = Number(paneRect?.left);
  const right = Number(paneRect?.right);
  const top = Number(paneRect?.top);
  const bottom = Number(paneRect?.bottom);

  return {
    left: Math.max(Number.isFinite(left) ? left : viewportRect.left, viewportRect.left),
    right: Math.min(Number.isFinite(right) ? right : viewportRect.right, viewportRect.right),
    top: Math.max(Number.isFinite(top) ? top : viewportRect.top, viewportRect.top),
    bottom: Math.min(Number.isFinite(bottom) ? bottom : viewportRect.bottom, viewportRect.bottom),
  };
};

const clampValue = (value, min, max) => {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
};

export const getReactionTrayFloatingPosition = ({
  rootRect,
  trayRect,
  paneRect,
  align = 'left',
  margin = 8,
}) => {
  const rootLeft = Number(rootRect?.left);
  const rootRight = Number(rootRect?.right);
  const rootTop = Number(rootRect?.top);
  const rootBottom = Number(rootRect?.bottom);
  const width = Number(trayRect?.width);
  const height = Number(trayRect?.height);
  const paneLeft = Number(paneRect?.left);
  const paneRight = Number(paneRect?.right);
  const paneTop = Number(paneRect?.top);
  const paneBottom = Number(paneRect?.bottom);

  if (
    !Number.isFinite(rootLeft) ||
    !Number.isFinite(rootRight) ||
    !Number.isFinite(rootTop) ||
    !Number.isFinite(rootBottom) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    !Number.isFinite(paneLeft) ||
    !Number.isFinite(paneRight) ||
    !Number.isFinite(paneTop) ||
    !Number.isFinite(paneBottom)
  ) {
    return { left: 0, top: 0 };
  }

  const safeMargin = Number.isFinite(Number(margin))
    ? Math.max(0, Number(margin))
    : 8;
  const minLeft = paneLeft + safeMargin;
  const maxLeft = paneRight - safeMargin - width;
  const minTop = paneTop + safeMargin;
  const maxTop = paneBottom - safeMargin - height;
  const preferredLeft =
    align === 'right' ? rootRight - width : rootLeft;
  const preferredTop = rootTop - height - 8;
  const fallbackTop = rootBottom + 8;
  const top =
    preferredTop >= minTop
      ? preferredTop
      : fallbackTop;

  return {
    left: Math.round(clampValue(preferredLeft, minLeft, maxLeft)),
    top: Math.round(clampValue(top, minTop, maxTop)),
  };
};

export function MessageReactionSummary({
  message,
  currentPlayerId,
  own = false,
}) {
  const reactionGroups = useMemo(
    () => getMessageReactionGroups(message, currentPlayerId),
    [currentPlayerId, message],
  );

  if (!reactionGroups.length) return null;

  return (
    <div
      className={own ? 'messageReactionSummary own' : 'messageReactionSummary'}
      aria-label="Message reactions"
    >
      {reactionGroups.map(({ reaction, count, isMine }) => (
        <span
          key={reaction}
          className={['messageReactionSummaryPill', isMine ? 'mine' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <span aria-hidden="true">{reaction}</span>
          {count > 1 ? <span>{count}</span> : null}
        </span>
      ))}
    </div>
  );
}

export default function MessageReactions({
  messageId,
  onReactMessage,
  isOpen = false,
  onOpenChange,
  onReply,
}) {
  const [showFullReactionPicker, setShowFullReactionPicker] = useState(false);
  const [reactionError, setReactionError] = useState('');
  const [isReacting, setIsReacting] = useState(false);
  const reactionRootRef = useRef(null);
  const reactionTrayRef = useRef(null);
  const reactionPickerRef = useRef(null);
  const floatingPositionRef = useRef({
    pickerLeft: 0,
    pickerTop: 0,
    trayLeft: 0,
    trayTop: 0,
  });
  const [floatingPosition, setFloatingPosition] = useState(
    floatingPositionRef.current,
  );

  const resetTrayClamp = useCallback(() => {
    const nextPosition = {
      pickerLeft: 0,
      pickerTop: 0,
      trayLeft: 0,
      trayTop: 0,
    };
    floatingPositionRef.current = nextPosition;
    setFloatingPosition(nextPosition);
  }, []);

  useEffect(() => {
    if (!isOpen && !showFullReactionPicker) return undefined;

    const closeTray = (event) => {
      if (reactionRootRef.current?.contains(event.target)) return;
      const replyActionZone = reactionRootRef.current?.closest('.messageActionZoneWithReply');
      if (replyActionZone?.contains(event.target)) return;
      onOpenChange?.(false);
      setShowFullReactionPicker(false);
      resetTrayClamp();
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      onOpenChange?.(false);
      setShowFullReactionPicker(false);
      resetTrayClamp();
    };

    document.addEventListener('mousedown', closeTray);
    document.addEventListener('touchstart', closeTray);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', closeTray);
      document.removeEventListener('touchstart', closeTray);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onOpenChange, resetTrayClamp, showFullReactionPicker]);

  useEffect(() => {
    onOpenChange?.(false);
    setShowFullReactionPicker(false);
    setReactionError('');
    resetTrayClamp();
  }, [messageId, onOpenChange, resetTrayClamp]);

  useLayoutEffect(() => {
    if (!isOpen) {
      resetTrayClamp();
      return undefined;
    }

    const updateTrayClamp = () => {
      const tray = reactionTrayRef.current;
      const root = reactionRootRef.current;
      if (!tray || !root) return;

      const pane =
        root?.closest?.('.chatBoxTop') ||
        root?.closest?.('.chatBox') ||
        document.documentElement;
      const paneRect = pane?.getBoundingClientRect?.() || {
        left: 0,
        right: window.innerWidth,
      };
      const rootRect = root.getBoundingClientRect();
      const isOwnMessage = Boolean(root.closest?.('.message.own'));
      const trayPosition = getReactionTrayFloatingPosition({
        rootRect,
        trayRect: tray.getBoundingClientRect(),
        paneRect: getVisiblePaneRect(paneRect),
        align: isOwnMessage ? 'right' : 'left',
        margin: 14,
      });

      const picker = reactionPickerRef.current;
      const pickerRect = picker?.getBoundingClientRect?.();
      const pickerPosition =
        showFullReactionPicker && pickerRect
          ? getReactionTrayFloatingPosition({
              rootRect: {
                left: trayPosition.left,
                right: trayPosition.left + tray.getBoundingClientRect().width,
                top: trayPosition.top,
                bottom: trayPosition.top + tray.getBoundingClientRect().height,
              },
              trayRect: pickerRect,
              paneRect: getVisiblePaneRect(paneRect),
              align: isOwnMessage ? 'right' : 'left',
              margin: 14,
            })
          : {
              left: floatingPositionRef.current.pickerLeft,
              top: floatingPositionRef.current.pickerTop,
            };

      const nextPosition = {
        pickerLeft: pickerPosition.left,
        pickerTop: pickerPosition.top,
        trayLeft: trayPosition.left,
        trayTop: trayPosition.top,
      };

      const currentPosition = floatingPositionRef.current;
      if (
        currentPosition.pickerLeft === nextPosition.pickerLeft &&
        currentPosition.pickerTop === nextPosition.pickerTop &&
        currentPosition.trayLeft === nextPosition.trayLeft &&
        currentPosition.trayTop === nextPosition.trayTop
      ) {
        return;
      }

      floatingPositionRef.current = nextPosition;
      setFloatingPosition(nextPosition);
    };

    updateTrayClamp();
    const frameId = window.requestAnimationFrame(updateTrayClamp);
    window.addEventListener('resize', updateTrayClamp);
    window.addEventListener('orientationchange', updateTrayClamp);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateTrayClamp);
      window.removeEventListener('orientationchange', updateTrayClamp);
    };
  }, [isOpen, resetTrayClamp, showFullReactionPicker]);

  const submitReaction = useCallback(
    async (reactionValue) => {
      if (isReacting) return;
      const reaction = normalizeMessageReaction(reactionValue);
      if (!messageId || !reaction) return;

      setReactionError('');
      setIsReacting(true);

      try {
        await onReactMessage({ messageId, reaction });
        onOpenChange?.(false);
        setShowFullReactionPicker(false);
      } catch (err) {
        setReactionError('Reaction failed. Try again.');
      } finally {
        setIsReacting(false);
      }
    },
    [isReacting, messageId, onOpenChange, onReactMessage],
  );

  const handlePickedReaction = useCallback(
    (eventOrEmojiObject, maybeEmojiObject) => {
      submitReaction(maybeEmojiObject?.emoji || eventOrEmojiObject?.emoji);
    },
    [submitReaction],
  );

  return (
    <>
      <div
        className={onReply ? 'messageReactionDock messageReplyDock' : 'messageReactionDock'}
        ref={reactionRootRef}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="messageReactionTrigger"
          aria-label="React to message"
          aria-expanded={isOpen ? 'true' : 'false'}
          onClick={() => {
            onOpenChange?.(!isOpen);
            setShowFullReactionPicker(false);
          }}
        >
          <span aria-hidden="true">{'\u263A'}</span>
        </button>

        {onReply ? (
          <button
            type="button"
            className="messageReactionTrigger messageReplyTrigger"
            aria-label="Reply to message"
            title="Reply"
            onClick={() => {
              onOpenChange?.(false);
              setShowFullReactionPicker(false);
              onReply();
            }}
          >
            <ReplyRoundedIcon />
          </button>
        ) : null}

        {isOpen ? (
          <div
            className="messageReactionTray"
            role="menu"
            ref={reactionTrayRef}
            style={{
              '--message-reaction-tray-left': `${floatingPosition.trayLeft}px`,
              '--message-reaction-tray-top': `${floatingPosition.trayTop}px`,
            }}
          >
            {QUICK_MESSAGE_REACTIONS.map(({ reaction, label }) => (
              <button
                key={reaction}
                type="button"
                className="messageReactionOption"
                onClick={() => submitReaction(reaction)}
                disabled={isReacting}
                aria-label={`React with ${label}`}
              >
                {reaction}
              </button>
            ))}
            <button
              type="button"
              className="messageReactionOption messageReactionMore"
              onClick={() => setShowFullReactionPicker((value) => !value)}
              disabled={isReacting}
              aria-label="Choose another reaction"
            >
              +
            </button>
          </div>
        ) : null}

        {showFullReactionPicker ? (
          <div
            className="messageReactionEmojiPicker"
            aria-label="More message reactions"
            ref={reactionPickerRef}
            style={{
              '--message-reaction-picker-left': `${floatingPosition.pickerLeft}px`,
              '--message-reaction-picker-top': `${floatingPosition.pickerTop}px`,
            }}
          >
            <AppEmojiPicker
              onEmojiClick={handlePickedReaction}
              pickerStyle={messageReactionPickerStyle}
            />
          </div>
        ) : null}
      </div>

      {reactionError ? (
        <div
          className="messageReactionError"
          role="status"
        >
          {reactionError}
        </div>
      ) : null}
    </>
  );
}
